import { Action } from '../types/types';
import {
	SET_SHOWBOARD_EXPANDED,
	SET_LIST_SORT_TYPE,
	SET_LIST_SHOW_PAST,
	SET_LIST_SHOW_ALL,
	SET_SHOW_CALENDAR,
	SET_SELECTED_CALENDAR_VIEW,
	SET_SELECTED_CALENDAR_OPTION,
	SET_MEDIA_FILTERS,
	SET_OPEN_CONTEXT_MENU_ID,
	SET_DRAGGING_PROPS,
	SET_STYLE_PACK_TYPE_FILTER,
	SHOW_DEPRECATION_WARNING,
	SET_TEMPORARY_EDITED_USER,
	SET_TEMPORARY_EDITED_USER_WEBRTC_ID,
	SET_DISPLAYED_STYLE_PACK_INFO,
	SCROLL_TO_STYLE_PACK_ID,
	SET_STYLE_PACK_CHANNEL_FILTER,
	SET_SOLUTION_CENTER_DRAWER_STATE,
	SET_SOLUTION_CENTER_CONTENT_STATE,
} from '../actions/user-interface';
import { SlidingMenuOption } from '../../components/general-components/sliding-menu';
import { MEDIA_FILTER_TYPES, MEDIA_SORT_TYPES } from '../../types/constants';
import { STYLE_PACK_FILTER, StylePackData } from '../../components/types/stylepacks';
import {
	SolutionCenterContent,
	SolutionCenterContentStateAction,
	SolutionCenterDrawerStateAction,
	SolutionCenterDrawerStates,
	SolutionCenterViews,
} from '../../types/solution-center';
import type {
	DragItem,
	RelativePosition,
	DropAreaData,
	DropCallbackData,
	DropCallback,
	UserInterfaceState,
	MediaFilters,
} from '../types/user-interface';

export type {
	DragItem,
	RelativePosition,
	DropAreaData,
	DropCallbackData,
	DropCallback,
	UserInterfaceState,
	MediaFilters,
} from '../types/user-interface';

export const calendarOptions = [
	{ label: 'Month', value: 'dayGridMonth' },
	{ label: 'Week', value: 'timeGridWeek' },
	{ label: 'Day', value: 'timeGridDay' },
];

export const DRAG_TYPE = {
	OVERLAY: 'overlay',
	FEED_MEDIA: 'media',
	FEED_MEDIA_AUDIO: 'audio',
	FEED_PRESENTER: 'presenter',
	FEED_EMPTY: 'empty',
	FEED_EMAIL: 'email',
	FEED_SCREENSHARE: 'screenshare',
	FEED_SIP_CALL: 'sipCall',
	FEED_SRT_STREAM: 'srtStream',
	FEED_ONSITE_LOCATION: 'onsiteLocation',
	TITLE: 'title',
	PROFILE_MASK: 'profileMask',
	// for easier syntax passing into drop area accepted types
	FEEDS: [
		'media',
		'presenter',
		'email',
		'screenshare',
		'title',
		'sipCall',
		'srtStream',
		'onsiteLocation',
	],
};

export const initialUserInterfaceState: UserInterfaceState = {
	isShowboardExpanded: false,
	listSortType: 'none',
	listShowPast: false,
	listShowAll: false,
	showCalendar: false,
	selectedCalendarView: calendarOptions[0].value,
	selectedCalendarOption: null,
	mediaFilters: {
		selectedFilterType: MEDIA_FILTER_TYPES.ALL,
		selectedSortType: MEDIA_SORT_TYPES.NONE,
	},
	openContextMenuId: null,
	globalIsDragging: null,
	idOfDragging: null,
	typeOfDragging: null,
	stylePackTypeFilter: undefined,
	stylepackChannelFilter: STYLE_PACK_FILTER.ALL,
	showDeprecationWarning: false,
	temporaryEditedUser: null,
	temporaryUserWebRtcId: null,
	displayedStylePackInfo: null,
	scrollToStylePackId: '',
	solutionCenterDrawerState: SolutionCenterDrawerStates.Closed,
	solutionCenterView: SolutionCenterViews.Home,
	solutionCenterContent: null,
};

const actions: Map<any, Function> = new Map([
	[
		SET_SHOWBOARD_EXPANDED,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			isShowboardExpanded: action.payload,
		}),
	],
	[
		SET_LIST_SORT_TYPE,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			listSortType: action.payload,
		}),
	],
	[
		SET_LIST_SHOW_PAST,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			listShowPast: action.payload,
		}),
	],
	[
		SET_LIST_SHOW_ALL,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			listShowAll: action.payload,
		}),
	],
	[
		SET_SHOW_CALENDAR,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			showCalendar: action.payload,
		}),
	],
	[
		SET_SELECTED_CALENDAR_VIEW,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			selectedCalendarView: action.payload,
		}),
	],
	[
		SET_SELECTED_CALENDAR_OPTION,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			selectedCalendarOption: action.payload,
		}),
	],
	[
		SET_MEDIA_FILTERS,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			mediaFilters: action.payload,
		}),
	],
	[
		SET_OPEN_CONTEXT_MENU_ID,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			openContextMenuId: action.payload,
		}),
	],
	[
		SET_DRAGGING_PROPS,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			...action.payload,
		}),
	],
	[
		SET_STYLE_PACK_TYPE_FILTER,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			stylePackTypeFilter: action.payload,
		}),
	],
	[
		SET_STYLE_PACK_CHANNEL_FILTER,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			stylepackChannelFilter: action.payload,
		}),
	],
	[
		SHOW_DEPRECATION_WARNING,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			showDeprecationWarning: action.payload,
		}),
	],
	[
		SET_TEMPORARY_EDITED_USER,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			temporaryEditedUser: action.payload,
		}),
	],
	[
		SET_TEMPORARY_EDITED_USER_WEBRTC_ID,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			temporaryUserWebRtcId: action.payload,
		}),
	],
	[
		SET_DISPLAYED_STYLE_PACK_INFO,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			displayedStylePackInfo: action.payload,
		}),
	],
	[
		SCROLL_TO_STYLE_PACK_ID,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			scrollToStylePackId: action.payload,
		}),
	],
	[
		SET_SOLUTION_CENTER_DRAWER_STATE,
		(state: UserInterfaceState, action: Action): UserInterfaceState => {
			const {
				payload: {
					drawer,
				},
			}: SolutionCenterDrawerStateAction = action;

			return {
				...state,
				solutionCenterDrawerState: drawer,
			};
		},
	],
	[
		SET_SOLUTION_CENTER_CONTENT_STATE,
		(state: UserInterfaceState, action: Action): UserInterfaceState => {
			const {
				payload: {
					content,
					view = SolutionCenterViews.Home,
				},
			}: SolutionCenterContentStateAction = action;

			// if there is content or a view, ensure the drawer is open
			const shouldOpen = !!action.payload.content || !!action.payload.view;
			const newD = shouldOpen ? SolutionCenterDrawerStates.Open : state.solutionCenterDrawerState;

			// if there is content, ensure the view is content
			const newV = content ? SolutionCenterViews.Content : view;

			return {
				...state,
				solutionCenterDrawerState: newD,
				solutionCenterView: newV,
				solutionCenterContent: action.payload.content,
			};
		},
	],
]);

export function UserInterfaceReducer(
	// eslint-disable-next-line default-param-last
	state: UserInterfaceState = initialUserInterfaceState,
	action: Action,
) {
	return actions.get(action.type)?.(state, action) || state;
}
